'use client';
import React, { useState } from 'react';

const tabs = [
  {
    id: 1,
    label: 'AppSec & Vulnerability Management',
    tag: '[01]',
    heading: 'Find what matters. Fix it before it ships.',
    description: 'Automated scanners produce noise. Our engineers produce signal. We integrate SAST, DAST, and SCA directly into your CI/CD pipelines and then do the work that tools cannot, triaging and enriching every CVE with business context so your teams know exactly what to fix, in what order, and why.',
    features: [
      'SAST, DAST, and SCA embedded into CI/CD, not bolted on after the fact.',
      'CVE triage and enrichment driven by business context, not just CVSS scores.',
      'Business-logic vulnerability identification that automated scanners miss entirely.',
      'Dependency and supply chain security across your full stack.',
      'Findings that come with remediation guidance, not just a report.',
    ],
  },
  {
    id: 2,
    label: 'Compliance & Continuous Compliance',
    tag: '[02]',
    heading: 'Compliant on day one. Compliant on day 365.',
    description: 'Most compliance engagements end when the certificate is issued. Ours do not. We build engineering-led programmes for SOC2, ISO 27001, DORA, and GDPR, implementing controls, collecting evidence, and staying embedded to ensure your posture never lapses between audit windows. Compliance that is continuous by design, not by accident.',
    features: [
      'SOC2 Type I/II and ISO 27001:2022 readiness, gap analysis, and certification support.',
      'DORA compliance: ICT risk management, incident response, and third-party oversight.',
      'GDPR: data protection by design, privacy impact assessments, and technical controls.',
      'Continuous control monitoring, evidence collected daily, not scrambled before audits.',
      'ISMS design, risk treatment, policy management, and internal audit support.',
    ],
  },
  {
    id: 3,
    label: 'Threat Modelling & Security Architecture',
    tag: '[03]',
    heading: 'Security designed in. Not discovered after launch.',
    description: 'Security flaws found in design cost a fraction of those found in production. Our engineers embed into your engineering process, reviewing new features, services, and infrastructure changes before they ship. We build risk-led security programmes that reflect your actual threat landscape, not generic industry checklists.',
    features: [
      'Threat modelling for new features, services, and infrastructure changes.',
      'Risk-led security programmes tailored to your organisation\'s specific threat profile.',
      'Zero-trust architecture design and implementation across cloud and on-premise.',
      'Security architecture review embedded into your design and sprint process.',
      'Attack surface analysis and security control gap identification.',
    ],
  },
  {
    id: 4,
    label: 'Continuous DevSecOps',
    tag: '[04]',
    heading: 'Embedded security at engineering speed.',
    description: 'We do not parachute in once a year. TechSonar engineers work as part of your team, in your sprints, your standups, your delivery process. Continuous pipeline hardening, ongoing monitoring, rapid remediation, and security reporting that gives leadership real visibility. Security that moves at the speed your business demands.',
    features: [
      'Embedded security engineers working inside your team, not alongside it.',
      'Continuous pipeline security validation and hardening.',
      'Ongoing monitoring, drift detection, and rapid remediation.',
      'Security reporting that gives leadership meaningful, actionable visibility.',
      'Security culture built into your engineering practice, not imposed on it.',
    ],
  },
  {
    id: 5,
    label: 'IaC & Pipeline Security',
    tag: '[05]',
    heading: 'Secure infrastructure before it is ever provisioned.',
    description: 'Misconfigured infrastructure is one of the most common causes of cloud security incidents. We harden your CI/CD pipelines and secure your Infrastructure as Code, catching policy violations, privilege escalations, and misconfigurations before they reach any environment. Prevention, not remediation.',
    features: [
      'IaC security for Terraform, Pulumi, CloudFormation, and Ansible.',
      'Pipeline hardening: secrets management, SBOM generation, and supply chain controls.',
      'Policy-as-code enforcement with OPA, Checkov, and custom rulesets.',
      'Pre-commit and pre-merge security gates that stop issues at source.',
      'Drift detection and automated remediation for infrastructure policy violations.',
    ],
  },
  {
    id: 6,
    label: 'Cloud & Platform Engineering',
    tag: '[06]',
    heading: 'Cloud platforms built secure from the ground up.',
    description: 'We design and implement secure cloud architectures across AWS, Azure, and GCP, covering identity and access management, network segmentation, workload protection, and cloud-native security controls. Platform engineering that treats security as a first-class requirement, not an afterthought.',
    features: [
      'Secure cloud architecture design across AWS, Azure, and GCP.',
      'Identity and access management: least-privilege, RBAC, and federated identity.',
      'Network segmentation, micro-segmentation, and zero-trust network access.',
      'Cloud-native security controls: GuardDuty, Defender for Cloud, Security Command Center.',
      'Kubernetes and container security: image scanning, runtime protection, and policy enforcement.',
    ],
  },
];

export default function PlatformSection() {
  const [activeTab, setActiveTab] = useState(0);
  const tab = tabs?.[activeTab];

  return (
    <section className="bg-[#050a0f] border-t border-[#222] py-20 lg:py-28">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
          <div className="lg:w-1/3">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">
              What we do
            </p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight">
              Impactful security across{' '}
              <span className="text-[#80edfa]">every layer of your stack</span>
            </h2>
          </div>
          <div className="lg:w-1/2 flex items-end">
            <p className="text-[#aaa] text-base leading-relaxed">
              From AppSec and CVE triage to continuous compliance, threat modelling, IaC security, and cloud platform engineering, TechSonar delivers security that makes a real difference. Embedded, continuous, and led by senior engineers who understand both security and how modern software is built.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-col gap-0">
          <div className="flex border-b border-[#222] overflow-x-auto">
            {tabs?.map((t, i) => (
              <button
                key={t?.id}
                onClick={() => setActiveTab(i)}
                className={`flex items-center gap-3 px-5 py-4 text-sm whitespace-nowrap transition-all duration-200 ${
                  activeTab === i
                    ? 'border-b-2 border-[#80edfa] text-[#f5f5f8] -mb-px'
                    : 'border-b-2 border-transparent text-[#666] hover:text-[#aaa]'
                }`}
              >
                <span className="font-mono-brand text-[0.65rem] text-[#80edfa] tracking-widest">{t?.tag}</span>
                <span className="font-sans uppercase tracking-wide text-xs">{t?.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="pt-10">
            <div className="flex flex-col lg:flex-row gap-16">
              {/* Left: Text */}
              <div className="lg:w-1/2 flex flex-col justify-center">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">
                  {tab?.tag}
                </p>
                <h3 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium mb-4">
                  {tab?.heading}
                </h3>
                <p className="text-[#aaa] text-sm leading-relaxed mb-6">
                  {tab?.description}
                </p>
                <ul className="flex flex-col gap-3 mb-8">
                  {tab?.features?.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <img
                        src="https://cdn.prod.website-files.com/68a77090b95f85ab69076143/68fba43b700c01c8947096af_checkbox.svg"
                        alt=""
                        className="w-4 h-4 mt-0.5 flex-shrink-0"
                      />
                      <span className="text-sm text-[#aaa]">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a href="#contact" className="btn-secondary inline-flex w-fit">
                  Talk to an expert
                  <img
                    src="https://cdn.prod.website-files.com/68a77090b95f85ab69076143/68fba25f4f2b74804892f1fa_arrow-right.svg"
                    alt=""
                    className="w-4 h-4"
                  />
                </a>
              </div>

              {/* Right: Key differentiators panel */}
              <div className="lg:w-1/2 flex flex-col gap-4">
                <div className="border border-[#222] bg-[#181b1f] p-6">
                  <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">Why TechSonar is different</p>
                  <p className="text-sm text-[#aaa] leading-relaxed">
                    Most security engagements end when the report is delivered. TechSonar engineers stay embedded, working alongside your team to ensure findings are remediated, controls are maintained, and your security posture improves continuously. Not fix-and-forget. Not point-in-time. Every day.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-[#222] bg-[#181b1f] p-5">
                    <div className="w-8 h-8 border border-[#80edfa]/30 flex items-center justify-center mb-3">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="#80edfa" strokeWidth="1.2"/>
                        <path d="M5 8l2 2 4-4" stroke="#80edfa" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest mb-1">Continuous</p>
                    <p className="text-xs text-[#666]">Security coverage every day of the year, not just at audit time.</p>
                  </div>
                  <div className="border border-[#222] bg-[#181b1f] p-5">
                    <div className="w-8 h-8 border border-[#80edfa]/30 flex items-center justify-center mb-3">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 2L10 6H14L11 9L12 13L8 11L4 13L5 9L2 6H6L8 2Z" stroke="#80edfa" strokeWidth="1.2" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest mb-1">Expert-led</p>
                    <p className="text-xs text-[#666]">Senior engineers only. No juniors, no automated-only outputs.</p>
                  </div>
                  <div className="border border-[#222] bg-[#181b1f] p-5">
                    <div className="w-8 h-8 border border-[#80edfa]/30 flex items-center justify-center mb-3">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <rect x="2" y="2" width="5" height="5" stroke="#80edfa" strokeWidth="1.2"/>
                        <rect x="9" y="2" width="5" height="5" stroke="#80edfa" strokeWidth="1.2"/>
                        <rect x="2" y="9" width="5" height="5" stroke="#80edfa" strokeWidth="1.2"/>
                        <rect x="9" y="9" width="5" height="5" stroke="#80edfa" strokeWidth="1.2"/>
                      </svg>
                    </div>
                    <p className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest mb-1">Embedded</p>
                    <p className="text-xs text-[#666]">Part of your team, not an external vendor dropping in once a year.</p>
                  </div>
                  <div className="border border-[#222] bg-[#181b1f] p-5">
                    <div className="w-8 h-8 border border-[#80edfa]/30 flex items-center justify-center mb-3">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 8h3l2-5 3 10 2-5h2" stroke="#80edfa" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest mb-1">Risk-led</p>
                    <p className="text-xs text-[#666]">Prioritised by real risk to your business, not generic compliance checklists.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
